import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Plus } from "lucide-react";
import TripFormDialog from "./TripFormDialog";
import TripDetailPanel from "./TripDetailPanel";
import { useTransportRefData } from "./shared/useTransportRefData";
import { PaginationBar } from "@/components/shared/PaginationBar";

export default function TripListTab() {
  const refData = useTransportRefData();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tripType, setTripType] = useState("all");
  const [status, setStatus] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = (pageNum = 1) => {
    setLoading(true);
    const params = { page: pageNum, page_size: 50 };
    if (search.trim()) params.search = search.trim();
    if (tripType !== "all") params.trip_type = tripType;
    if (status !== "all") params.status = status;
    client
      .get("/transport-log/trips", { params })
      .then((res) => {
        setTrips(res.data.items);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(1), [search, tripType, status]);

  return (
    <div className="space-y-4" data-testid="trip-list-tab">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              data-testid="trip-search-input"
              placeholder="Search Trip ID or Rig..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-64"
            />
          </div>
          <Select value={tripType} onValueChange={setTripType}>
            <SelectTrigger data-testid="trip-type-filter" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Rig">Rig</SelectItem>
              <SelectItem value="Material">Material</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="trip-status-filter" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="In Transit">In Transit</SelectItem>
              <SelectItem value="Completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="trip-add-button" onClick={() => setFormOpen(true)} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="h-4 w-4 mr-1.5" /> Add Trip
        </Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="trip-table">
          <TableHeader>
            <TableRow>
              <TableHead>Trip ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Rig / Items</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>Last Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : trips.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                  No trips found.
                </TableCell>
              </TableRow>
            ) : (
              trips.map((t) => (
                <TableRow key={t.id} data-testid={`trip-row-${t.id}`} className="cursor-pointer" onClick={() => setSelectedId(t.id)}>
                  <TableCell className="font-mono text-xs">{t.trip_id}</TableCell>
                  <TableCell>{t.trip_type}</TableCell>
                  <TableCell>{t.from_site_label}</TableCell>
                  <TableCell>{t.to_site_label || t.to_site_free_text}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {t.trip_type === "Rig" ? t.rig_label : `${(t.items || []).length} item(s)`}
                  </TableCell>
                  <TableCell>
                    <Badge className={t.status === "Completed" ? "bg-emerald-600" : "bg-amber-500"}>{t.status}</Badge>
                  </TableCell>
                  <TableCell>{new Date(t.start_date).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell>{t.last_location_update_at ? new Date(t.last_location_update_at).toLocaleString("en-IN") : "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="trip-pagination" />

      {formOpen && (
        <TripFormDialog
          sites={refData.sites}
          rigs={refData.rigs}
          employees={refData.employees}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            load(1);
          }}
        />
      )}
      {selectedId && (
        <TripDetailPanel
          tripId={selectedId}
          sites={refData.sites}
          rigs={refData.rigs}
          employees={refData.employees}
          onClose={() => setSelectedId(null)}
          onChanged={() => load(page)}
        />
      )}
    </div>
  );
}
